import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["appHub", "children", "withoutSidebar", "onReady", "onContentReady", "onContentError"],
  _excluded2 = ["handleMessage", "show"],
  _excluded3 = ["handleMessage", "show"],
  _excluded4 = ["handleMessage", "show"],
  _excluded5 = ["collaborationSidebarState", "onUpdateCollaborationSidebarState"];
import I18n from 'I18n';
import { useEffect, useRef, useState } from 'react';
import { IS_COMMENT_MODE_ACTIVE } from '../constants/CollaborationSidebarStateProperties';
import CollaborationSidebarStateContext from '../contexts/CollaborationSidebarStateContext';
import UserSettingsProvider from '../contexts/UserSettingsProvider';
import usePersistPropsToCollaborationSidebarState from '../hooks/usePersistPropsToCollaborationSidebarState';
import { showWarningNotification } from '../utils/notifications';
import { getIsCommentingUrl } from '../utils/urls';
import AsyncOnboardingTour from 'collaboration-sidebar/features/deepCommenting/components/AsyncOnboardingTour';
import CommentModeIndicator from 'collaboration-sidebar/features/deepCommenting/components/CommentModeIndicator';
import { CommentObjectType } from '../constants';
import CollaborationSidebarPropsContext from '../contexts/CollaborationSidebarPropsContext';
import useEmbeddedComments from '../features/deepCommenting/hooks/useEmbeddedComments';
import useHasUnreadComments from '../hooks/useHasUnreadComments';
import useSidebarIframeHandlers from '../hooks/useSidebarIframeHandlers';
import useConfirmDeleteCommentModal from 'collaboration-sidebar/hooks/modals/useConfirmDeleteCommentModal';
import useNotificationSettingsModal from 'collaboration-sidebar/hooks/modals/useNotificationSettingsModal';
import useTaskDeleteModal from 'collaboration-sidebar/hooks/modals/useTaskDeleteModal';
import useSidebarOpenCloseHandlers from '../hooks/useSidebarOpenCloseHandlers';
import useHasSidebarPermission from '../hooks/useHasSidebarPermission';
import AsyncMessageHandlers from './MessageHandlers/AsyncMessageHandlers';
import Modals from './Modals';
import EmbeddedCommentSetup from './EmbeddedCommentSetup';
import usePollForCommentsChanges from '../hooks/usePollForCommentsChanges';
import useOnboardingTour from '../hooks/useOnboardingTour';
import { COMMENT_ONBOARDING_TOUR_ID } from '../constants/tourIDs';
import { useCollaborationSidebarState } from '../hooks';
import useModuleScrollAndHighlight from '../features/deepCommenting/hooks/useModuleScrollAndHighlight';
import { UsageTracker } from '../utils/usageTracker';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
function ControlledCollaborationSidebarProvider(_ref) {
  let {
      appHub,
      children,
      withoutSidebar,
      onReady,
      onContentReady,
      onContentError
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const {
    collaborationSidebarState,
    onUpdateCollaborationSidebarState
  } = useCollaborationSidebarState();
  const didEmitOnReady = useRef(false);
  const hasShownNoPermissionWarning = useRef(false);
  const pageContentRef = useRef(null);
  const sendMessageRef = useRef();
  const onMessageRef = useRef(() => {});
  const [messageHandlersReady, setMessageHandlersReady] = useState(false);
  useEffect(() => {
    if (messageHandlersReady && onReady && !didEmitOnReady.current) {
      didEmitOnReady.current = true;
      onReady();
    }
  }, [messageHandlersReady, onReady]);
  const {
    objectType,
    objectId,
    openLeftEditor,
    closeLeftEditor,
    onCommentsUpdated,
    embeddedCommentsProps,
    commentingDisabled,
    onScrollToModule,
    onHighLightModuleChange
  } = rest;
  const expandableSidebarRef = useRef(null);
  const iframeHandlerProps = useSidebarIframeHandlers({
    withoutSidebar,
    isOpen: collaborationSidebarState.isOpen,
    sendMessageRef,
    onContentReady,
    onContentError
  });
  const hasSidebarPermission = useHasSidebarPermission({
    appHub
  });
  useEffect(() => {
    if (!hasShownNoPermissionWarning.current && !hasSidebarPermission && getIsCommentingUrl()) {
      showWarningNotification({
        title: I18n.text('collaborationSidebar.notifications.failure.missingPermissionCommentingTitle'),
        message: I18n.text('collaborationSidebar.notifications.failure.missingPermissionCommentingBody'),
        sticky: true
      });
      hasShownNoPermissionWarning.current = true;
    }
  }, [hasSidebarPermission]);
  const {
    hasUnreadComments,
    unreadCommentsCount,
    markCommentAsRead,
    refetchComments
  } = useHasUnreadComments(objectType, objectId);
  const {
    isCommentModeActive
  } = collaborationSidebarState;
  usePollForCommentsChanges({
    objectId,
    objectType,
    onUpdateCollaborationSidebarState,
    hasSidebarPermission,
    isOpen: collaborationSidebarState.isOpen,
    assetsLastUpdated: collaborationSidebarState.assetsLastUpdated
  });
  const {
    handleClose
  } = useSidebarOpenCloseHandlers({
    onUpdateCollaborationSidebarState,
    openLeftEditor,
    closeLeftEditor
  });
  const {
    hideEmbeddedComments,
    setHideEmbeddedComments,
    setModuleReply,
    removeModuleReply,
    startCommentMode,
    cancelCommentMode
  } = useEmbeddedComments({
    collaborationSidebarState,
    onUpdateCollaborationSidebarState,
    navigateTo: embeddedCommentsProps && embeddedCommentsProps.indicatorNavigateTo
  });
  const _useConfirmDeleteComm = useConfirmDeleteCommentModal({
      sendMessageRef,
      isSendMessageReady: iframeHandlerProps.isReady
    }),
    {
      handleMessage: handleConfirmDeleteCommentModalMessage,
      show: showDeleteCommentModal
    } = _useConfirmDeleteComm,
    confirmDeleteCommentModalProps = _objectWithoutPropertiesLoose(_useConfirmDeleteComm, _excluded2);
  const _useNotificationSetti = useNotificationSettingsModal({
      sendMessageRef,
      isSendMessageReady: iframeHandlerProps.isReady
    }),
    {
      handleMessage: handleNotificationSettingsModalMessage,
      show: showNotificationSettingsModal
    } = _useNotificationSetti,
    notificationSettingsModalProps = _objectWithoutPropertiesLoose(_useNotificationSetti, _excluded3);
  const _useTaskDeleteModal = useTaskDeleteModal({
      sendMessageRef,
      isSendMessageReady: iframeHandlerProps.isReady
    }),
    {
      handleMessage: handleTaskUpdatePanelMessage,
      show: showTaskDeleteModal
    } = _useTaskDeleteModal,
    taskDeleteModalProps = _objectWithoutPropertiesLoose(_useTaskDeleteModal, _excluded4);
  const showCommentingTour = useOnboardingTour({
    objectType,
    tourId: COMMENT_ONBOARDING_TOUR_ID,
    featureIsVisible: objectType === CommentObjectType.COMMENTING_SANDBOX && !commentingDisabled && !collaborationSidebarState.isOpen
  });
  useModuleScrollAndHighlight({
    collaborationSidebarState,
    onScrollToModule,
    onHighLightModuleChange
  });
  const [isPageContentRefSet, setIsPageContentRefSet] = useState(false);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(AsyncMessageHandlers, {
      collaborationSidebarState: collaborationSidebarState,
      onUpdateCollaborationSidebarState: onUpdateCollaborationSidebarState
      // @ts-expect-error ts-migrate(2322) FIXME: Type '({ payload }: $tsMigrateAny) => void' is not... Remove this comment to see the full error message
      ,
      handleTaskUpdatePanelMessage: handleTaskUpdatePanelMessage
      // @ts-expect-error ts-migrate(2322) FIXME: Type '({ payload }: $tsMigrateAny) => void' is not... Remove this comment to see the full error message
      ,
      handleConfirmDeleteCommentModalMessage: handleConfirmDeleteCommentModalMessage,
      handleNotificationSettingsModalMessage: handleNotificationSettingsModalMessage,
      markCommentAsRead: markCommentAsRead,
      handleClose: handleClose,
      refetchComments: refetchComments,
      onCommentsUpdated: onCommentsUpdated,
      preLoadIframe: iframeHandlerProps.preLoadIframe,
      setHideEmbeddedComments: setHideEmbeddedComments,
      hideEmbeddedComments: hideEmbeddedComments,
      sendMessageRef: sendMessageRef,
      setModuleReply: setModuleReply,
      removeModuleReply: removeModuleReply,
      onMessageRef: onMessageRef,
      setMessageHandlersReady: setMessageHandlersReady
    }), /*#__PURE__*/_jsx(CollaborationSidebarPropsContext.Provider, {
      value: Object.assign({}, rest, {
        pageContentRef,
        isPageContentRefSet,
        setIsPageContentRefSet,
        hasSidebarPermission,
        expandableSidebarRef,
        hasUnreadComments,
        unreadCommentsCount,
        withoutSidebar,
        isModalOpen: showDeleteCommentModal || showTaskDeleteModal,
        iframe: iframeHandlerProps,
        onMessageRef,
        messageHandlersReady,
        startCommentMode,
        cancelCommentMode
      }),
      children: /*#__PURE__*/_jsxs(UserSettingsProvider, {
        objectType: objectType,
        objectId: objectId,
        children: [/*#__PURE__*/_jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsx(EmbeddedCommentSetup, {}), /*#__PURE__*/_jsx(Modals
          // @ts-expect-error ts-migrate(2739) FIXME: Type '{ failed: any; formattedBody: any; loading: ... Remove this comment to see the full error message
          , {
            confirmDeleteCommentModalProps: confirmDeleteCommentModalProps,
            taskDeleteModalProps: taskDeleteModalProps,
            notificationSettingsModalProps: notificationSettingsModalProps,
            showDeleteCommentModal: showDeleteCommentModal,
            showNotificationSettingsModal: showNotificationSettingsModal,
            showTaskDeleteModal: showTaskDeleteModal,
            withoutSidebar: !!withoutSidebar,
            isReady: messageHandlersReady,
            IFrameError: iframeHandlerProps.IFrameError
          })]
        }), children, showCommentingTour && /*#__PURE__*/_jsx(AsyncOnboardingTour, {
          tourId: COMMENT_ONBOARDING_TOUR_ID
        }), isCommentModeActive && /*#__PURE__*/_jsx(CommentModeIndicator, {
          onCancel: () => {
            onUpdateCollaborationSidebarState({
              [IS_COMMENT_MODE_ACTIVE]: false
            });
            UsageTracker.track('collabSidebarInteraction', {
              action: 'close comment mode'
            });
          }
        })]
      })
    })]
  });
}
export default function CollaborationSidebarProviderWithState(_ref2) {
  let {
      collaborationSidebarState,
      onUpdateCollaborationSidebarState
    } = _ref2,
    rest = _objectWithoutPropertiesLoose(_ref2, _excluded5);
  const collaborationSidebarStateWithPersistedProps = usePersistPropsToCollaborationSidebarState({
    collaborationSidebarState,
    onUpdateCollaborationSidebarState,
    collaborationSidebarProps: rest
  });
  return /*#__PURE__*/_jsx(CollaborationSidebarStateContext.Provider, {
    value: {
      collaborationSidebarState: collaborationSidebarStateWithPersistedProps,
      onUpdateCollaborationSidebarState
    },
    children: /*#__PURE__*/_jsx(ControlledCollaborationSidebarProvider, Object.assign({}, rest))
  });
}